import React, { useState } from "react";
import CountryList from "../CountryList";
import { Spinner } from "react-bootstrap";
import { googleApi } from "../../services/api";
import { FaImages, FaSearch } from "react-icons/fa";

const CompetitorScreenshots = () => {
  const [keyword, setKeyword] = useState("");
  const [country, setCountry] = useState("us");
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedScreenshot, setSelectedScreenshot] = useState(null);

  const validateImageURL = (url) => {
    if (!url) return null;
    if (!url.startsWith("http")) return `https:${url}`;
    return url;
  };

  const preloadImages = async (urls) => {
    const loadImage = (url) =>
      new Promise((resolve) => {
        if (!url) return resolve();
        const img = new Image();
        img.src = validateImageURL(url);
        img.onload = resolve;
        img.onerror = resolve;
      });
    await Promise.all(urls.map(loadImage));
  };

  const handleSearch = async () => {
    if (!keyword.trim()) {
      alert("Please enter a keyword!");
      return;
    }

    setLoading(true);
    setError("");
    setApps([]);

    try {
      const res = await googleApi.post("keyword-search/", {
        keyword: keyword.trim(),
        country,
      });

      const data = res.data;

      if (Array.isArray(data) && data.length > 0) {
        const cleanedData = data.map((app) => ({
          ...app,
          icon: validateImageURL(app.icon),
          screenshots: (app.screenshots || [])
            .map(validateImageURL)
            .filter(Boolean),
        }));

        const allImages = cleanedData.flatMap((app) => [
          app.icon,
          ...(app.screenshots || []),
        ]);
        await preloadImages(allImages);

        setApps(cleanedData);
      } else {
        setError("No results found for this keyword.");
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        "Error fetching screenshots. Please try again.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (screenshot) => setSelectedScreenshot(screenshot);
  const closeModal = () => setSelectedScreenshot(null);

  return (
    <div className="container mt-4">
      {/* Search Section */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-8 border border-gray-100">
        <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
          <FaImages className="mr-3 text-pink-500" />
          Competitor Screenshots
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
            <div className="md:col-span-5">
                <label className="block text-sm font-medium text-gray-700 mb-1">Enter App Keyword</label>
                <input
                    type="text"
                    placeholder="e.g. Photo Editor"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    className="w-full bg-white border border-gray-300 rounded-md py-2 px-3 text-sm text-gray-700 placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition duration-200"
                    disabled={loading}
                />
            </div>
            <div className="md:col-span-5">
                <label className="block text-sm font-medium text-gray-700 mb-1">Select Country</label>
                <CountryList
                    onSelect={(selectedCountry) => setCountry(selectedCountry)}
                />
            </div>
            <div className="md:col-span-2">
                 <button
                    onClick={handleSearch}
                    className="w-full bg-pink-600 hover:bg-pink-700 text-white font-bold py-2 px-4 rounded-md shadow transition duration-200 flex items-center justify-center disabled:opacity-70 disabled:cursor-not-allowed"
                    disabled={loading}
                    >
                    {loading ? (
                        <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                    ) : (
                        <FaSearch className="mr-2" />
                    )}
                    {loading ? "Loading..." : "Search"}
                 </button>
            </div>
        </div>
      </div>

      {/* Loading Indicator */}
      {loading && (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "200px" }}
        >
          <Spinner animation="border" role="status" />
          <span className="ms-2">Loading screenshots...</span>
        </div>
      )}

      {/* Error Message */}
      {error && <div className="alert alert-danger text-center">{error}</div>}

      {/* Results Section */}
      {!loading && apps.length > 0 && (
        <div className="container mt-4">
          <div className="row g-4">
            {apps.map((app, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <div className="card shadow border-0 h-100">
                  <div className="card-body">
                    {/* App Header */}
                    <div className="d-flex align-items-center mb-3">
                      <img
                        src={app.icon || "/placeholder-icon.png"}
                        alt={app.title}
                        onError={(e) => (e.currentTarget.src = "/placeholder-icon.png")}
                        className="rounded me-3"
                        style={{
                          width: "60px",
                          height: "60px",
                          objectFit: "cover",
                          border: "2px solid #e0e0e0",
                        }}
                      />
                      <div>
                        <h6 className="mb-0 fw-semibold">{app.title}</h6>
                        <small className="text-muted">{app.genre}</small>
                      </div>
                    </div>

                    {/* Screenshots */}
                    <div className="d-flex flex-wrap gap-2">
                      {app.screenshots?.length > 0 ? (
                        app.screenshots.map((screenshot, i) => (
                          <img
                            key={i}
                            src={screenshot}
                            alt={`Screenshot ${i + 1}`}
                            loading="lazy"
                            onError={(e) => (e.currentTarget.style.display = "none")}
                            onClick={() => openModal(screenshot)}
                            className="rounded shadow-sm"
                            style={{
                              width: "32%",
                              height: "110px",
                              objectFit: "cover",
                              cursor: "pointer",
                              transition: "transform 0.2s ease",
                            }}
                            onMouseOver={(e) =>
                              (e.currentTarget.style.transform = "scale(1.04)")
                            }
                            onMouseOut={(e) =>
                              (e.currentTarget.style.transform = "scale(1)")
                            }
                          />
                        ))
                      ) : (
                        <p className="text-muted small">No screenshots available</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal */}
      {selectedScreenshot && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
          style={{ backgroundColor: "rgba(0,0,0,0.8)", zIndex: 9999 }}
          onClick={closeModal}
        >
          <div
            className="position-relative"
            style={{ maxWidth: "90%", maxHeight: "90%" }}
          >
            <button
              onClick={closeModal}
              className="btn btn-light position-absolute top-0 end-0 m-2 rounded-circle fw-bold"
              style={{ width: "35px", height: "35px" }}
            >
              ✕
            </button>
            <img
              src={selectedScreenshot}
              alt="Screenshot"
              className="img-fluid rounded shadow-lg"
              style={{ maxHeight: "90vh", objectFit: "contain" }}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CompetitorScreenshots;
